import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FieldType, FormlyModule } from '@ngx-formly/core';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { debounceTime, distinctUntilChanged, map, Subscription, takeUntil, tap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { DateHelperService } from '../../services/date-helper.service';

@Component({
  selector: 'formly-field-cron',
  template: `
    <div class="block float-left w-full">
      @if (offset < 0) {
        <mat-error class="pb-4">Let op: De begindatum moet eerder zijn dan de einddatum.</mat-error>
      }
      @for (field of field.fieldGroup; track $index) {
        <formly-field [field]="field" [class]="field.props.className"></formly-field>
      }
      @if (field.props.description) {
        <mat-hint class="inline-block mb-2">{{ field.props.description }}</mat-hint>
      }
    </div>
  `,
  imports: [
    ReactiveFormsModule,
    MatAutocompleteModule,
    FormlyModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyFieldCron extends FieldType implements OnInit {
  modulesStore = inject(ModulesStore);
  generatedForms$ = toObservable(this.modulesStore.generatedForms);
  private destroy$ = injectDestroy();
  offset = 0;
  firstChange = true;
  startDateWatcher: Subscription;
  private readonly fullDayStartTime = '00:00';
  private readonly fullDayEndTime = '23:59';
  private readonly defaultDurationSeconds = 86400; // 24 hours

  constructor() {
    super();
  }

  ngOnInit() {
    this.updateTimecontrollers();

    // Watch for changes in startDate, startTime and endTime
    const startDateControl = this.field.formControl.get('startDate');
    const startTimeControl = this.field.formControl.get('startTime');
    const endTimeControl = this.field.formControl.get('endTime');

    // Combine startDate and startTime
    startTimeControl?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        tap(startTime => {
          this.updateDateWithTime('startDate', startTime);
        })
      )
      .subscribe();

    // Calculate endDate based on startDate and times
    endTimeControl?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        tap(() => {
          this.calculateEndDate();
        })
      )
      .subscribe();

    startDateControl?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        // The Material datepicker (LuxonDateAdapter) and our string normalizer alternately rewrite
        // this control with the SAME instant but a different type (Luxon DateTime <-> ISO string).
        // A reference-based distinctUntilChanged never collapses that, so every rewrite re-enters the
        // handler and patches related fields again -> infinite valueChanges recursion (stack overflow).
        // Deduping on the normalized string makes both representations compare equal and breaks the loop.
        map(emittedValue => this.toComparableDate(emittedValue, this.shouldUseFullDay())),
        distinctUntilChanged(),
        tap(normalized => {
          if (normalized == null) return;
          this.normalizeDateControl(startDateControl, this.shouldUseFullDay());
          this.updateStartDateRelatedFields(startDateControl.value);
          this.calculateEndDate();
        })
      )
      .subscribe();

    this.field.formControl.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        debounceTime(300),
        tap(values => {
          this.field.formControl.get('offset').patchValue(this.generateOffset(values));
          this.validateOffset();
          // add watcher on value change because of dynamic field creation
          this.createStartDateWatcher();
        })
      )
      .subscribe();

    this.initializeFieldGroup();
    this.applyFullDayTimeDefaults();

    // check the related checkboxes if startDate is set (prefill), but no days-of-week are selected
    if (
      this.field.formControl.value.startDate != null &&
      this.field.formControl.get('days-of-week')?.value?.length === 0
    ) {
      this.updateStartDateRelatedFields(this.field.formControl.value.startDate);
    }
  }

  private updateTimecontrollers() {
    if (this.shouldUseFullDay()) {
      this.applyFullDayTimeDefaults();
      return;
    }

    if (
      this.field.formControl.get('startTime').value == null &&
      this.field.formControl.get('startDate').value != null
    ) {
      const date = new Date(this.field.formControl.get('startDate').value);
      const dateAndTime = DateHelperService.dateTimeToDateAndTime(DateHelperService.toLocalISOString(date));
      this.field.formControl.get('startTime').patchValue(dateAndTime.time);
      this.normalizeDateControl(this.field.formControl.get('startDate'));
    }

    if (this.field.formControl.get('endTime').value == null && this.field.formControl.get('endDate').value != null) {
      const date = new Date(this.field.formControl.get('endDate').value);
      const dateAndTime = DateHelperService.dateTimeToDateAndTime(DateHelperService.toLocalISOString(date));
      this.field.formControl.get('endTime').patchValue(dateAndTime.time);
      this.normalizeDateControl(this.field.formControl.get('endDate'));
    }

    if (this.field.formControl.get('endDate').value == null && this.field.formControl.get('endTime').value != null) {
      this.calculateEndDate();
    }
  }

  private updateDateWithTime(dateField: string, timeString: string) {
    const dateControl = this.field.formControl.get(dateField);
    if (!dateControl?.value || !timeString) return;

    const date = new Date(dateControl.value);
    const [hours, minutes] = timeString.split(':').map(Number);

    date.setHours(hours, minutes, 0, 0);
    dateControl.patchValue(DateHelperService.toLocalISOString(date));
  }

  private calculateEndDate() {
    const startDate = this.field.formControl.get('startDate')?.value;
    const startTime = this.field.formControl.get('startTime')?.value;
    const endTime = this.field.formControl.get('endTime')?.value;

    if (!startDate || !startTime || !endTime) return;

    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);

    const startDateTime = new Date(startDate);
    startDateTime.setHours(startHours, startMinutes, 0, 0);

    const endDateTime = new Date(startDate);
    endDateTime.setHours(endHours, endMinutes, 0, 0);

    // If end time is earlier than start time, add one day to end date
    if (endHours < startHours || (endHours === startHours && endMinutes <= startMinutes)) {
      endDateTime.setDate(endDateTime.getDate() + 1);
    }

    this.field.formControl.get('endDate')?.patchValue(DateHelperService.toLocalISOString(endDateTime));
  }

  private initializeFieldGroup() {
    this.field.fieldGroup = this.field.fieldGroup.map(field => {
      if (this.field.props.required) {
        field.props.required = this.field.props.required;
      }

      if (this.props.cronRequired === true && field.key === 'repeat') {
        this.removeNoRepeatOption(field);
      }

      if (this.props.dateRange === false) {
        if (field.key === 'endDate') {
          field.className = 'hidden';
          field.props.required = false;
        }
        if (field.key === 'startDate') {
          field.className = 'w-full inline-block';
        }
      }

      if (this.shouldUseFullDay() && (field.key === 'startTime' || field.key === 'endTime')) {
        field.className = 'hidden';
        field.props.required = false;
      }

      if (this.props.endTimeOptional === true && field.key === 'endTime') {
        field.props.required = false;
      }

      return field;
    });
  }

  removeNoRepeatOption(field) {
    const moduleId = this.options.formState?.entity?.id;

    if (moduleId) {
      this.generatedForms$
        .pipe(
          takeUntil(this.destroy$),
          map(forms => forms[moduleId]),
          distinctUntilChanged((prev, curr) => {
            if (this.firstChange) {
              this.firstChange = false;
              return false;
            }
            return JSON.stringify(prev) === JSON.stringify(curr);
          }),
          tap(() => {
            field.props.options = (field.props.options as Array<{ value: any; label: string }>).filter(option => {
              return option.value !== 'no-repeat';
            });

            // Only set defaults when the current value is missing or invalid ('no-repeat'),
            // so existing saved values are preserved when editing entries
            const currentRepeat = this.field.formControl.get(field.key)?.value;
            if (!currentRepeat || currentRepeat === 'no-repeat') {
              this.field.formControl.get(field.key)?.patchValue('Wekelijks');
            }

            const currentRepeatTypeEnd = this.field.formControl.get('repeat-type-end')?.value;
            if (!currentRepeatTypeEnd) {
              this.field.formControl.get('repeat-type-end')?.patchValue('no-end-date');
            }
          })
        )
        .subscribe();
    }
  }

  createStartDateWatcher() {
    if (this.startDateWatcher) this.startDateWatcher.unsubscribe();
    if (this.field.formControl.get('repeat-type') === null) {
      return;
    }

    this.startDateWatcher = this.field.formControl
      .get('repeat-type')
      .valueChanges.pipe(
        takeUntil(this.destroy$),
        tap(() => {
          requestAnimationFrame(() => {
            this.updateStartDateRelatedFields(this.field.formControl.value.startDate);
          });
        })
      )
      .subscribe();
  }

  updateStartDateRelatedFields(startDate) {
    if (startDate === undefined) {
      return;
    }

    startDate = new Date(startDate);
    const startDayOfWeek = startDate.getDay();
    const startDayOfMonth = startDate.getDate();
    const startMonth = startDate.getMonth() + 1; // Months are 0-indexed

    const repeatType = this.field.formControl.get('repeat').value;

    if (repeatType === 'Wekelijks') {
      // Initialize weekly interval to 1 if not set
      const weeklyControl = this.field.formControl.get('weekly');
      if (weeklyControl && !weeklyControl.value) {
        weeklyControl.patchValue('1');
      }

      // Initialize days-of-week based on startDate (only when not explicitly multi-selected)
      const daysOfWeek = this.field.formControl.get('days-of-week');
      if (daysOfWeek && (daysOfWeek.value?.length === 0 || daysOfWeek.value?.length === 1)) {
        this.patchArrayIfChanged('days-of-week', [startDayOfWeek.toString()]);
      }
    } else if (repeatType === 'Maandelijks' && this.field.formControl.get('days-of-month')) {
      this.patchArrayIfChanged('days-of-month', [startDayOfMonth.toString()]);
    } else if (repeatType === 'Jaarlijks' && this.field.formControl.get('months-of-year')) {
      this.patchArrayIfChanged('months-of-year', [startMonth.toString()]);
    }
  }

  /**
   * Patch an array control only when the value actually changes, so an unchanged re-computation
   * does not re-emit valueChanges and pump the parent form group (which re-hydrates the datepicker).
   */
  private patchArrayIfChanged(controlName: string, next: string[]) {
    const control = this.field.formControl.get(controlName);
    if (!control) return;
    const current = control.value as (string | number)[] | null;
    if (Array.isArray(current) && current.length === next.length && current.every((value, index) => String(value) === next[index])) {
      return;
    }
    control.patchValue(next);
  }

  generateOffset(values: any): string {
    // generate offset in s startDate - endDate
    if (!values.startDate) {
      return '0';
    }

    // When endTime is optional and not provided, default to 24 hours
    if (!values.endDate && this.props.endTimeOptional === true) {
      this.offset = this.defaultDurationSeconds;
      return this.defaultDurationSeconds.toString();
    }

    if (!values.endDate) {
      return '0';
    }

    const startDate = new Date(values.startDate);
    const endDate = new Date(values.endDate);
    const offset = (endDate.getTime() - startDate.getTime()) / 1000;

    this.offset = offset;
    return offset.toString();
  }

  validateOffset() {
    if (this.props.dateRange === false) {
      this.field.formControl.setErrors(null);
    }

    if (this.offset < 0) {
      this.field.formControl.setErrors({ invalidOffset: true });
    } else {
      this.field.formControl.setErrors(null);
    }
  }

  private shouldUseFullDay(): boolean {
    return this.props.fullDay === true;
  }

  /**
   * Normalize a date form control value to a local ISO string without timezone.
   * Handles Luxon DateTime objects (from datepicker) and timezone strings (from API).
   * Returns true if the value was changed (patched), false if already normalized.
   */
  private normalizeDateControl(control: AbstractControl, resetToMidnight = false): boolean {
    if (!control?.value) return false;
    const raw = control.value?.ts != null ? control.value.ts : control.value;
    const date = new Date(raw);
    if (resetToMidnight) {
      date.setHours(0, 0, 0, 0);
    }
    const normalized = DateHelperService.toLocalISOString(date);
    if (control.value !== normalized) {
      control.patchValue(normalized);
      return true;
    }
    return false;
  }

  /**
   * Map a date control value to a single comparable string. The picker emits Luxon DateTime
   * objects while the API/normalizer use ISO strings; both are reduced to the same local-ISO
   * string so distinctUntilChanged can collapse the Luxon<->string type ping-pong.
   */
  private toComparableDate(value: unknown, resetToMidnight = false): string | null {
    if (value == null) return null;
    const raw = (value as { ts?: number })?.ts != null ? (value as { ts: number }).ts : (value as string | number | Date);
    const date = new Date(raw);
    if (isNaN(date.getTime())) return null;
    if (resetToMidnight) {
      date.setHours(0, 0, 0, 0);
    }
    return DateHelperService.toLocalISOString(date);
  }

  private applyFullDayTimeDefaults() {
    if (!this.shouldUseFullDay()) {
      return;
    }

    const startTimeControl = this.field.formControl.get('startTime');
    const endTimeControl = this.field.formControl.get('endTime');

    if (startTimeControl && startTimeControl.value !== this.fullDayStartTime) {
      startTimeControl.patchValue(this.fullDayStartTime);
    }

    if (endTimeControl && endTimeControl.value !== this.fullDayEndTime) {
      endTimeControl.patchValue(this.fullDayEndTime);
    }

    // Normalize startDate to strip timezone (endDate is normalized by calculateEndDate)
    this.normalizeDateControl(this.field.formControl.get('startDate'), true);

    this.calculateEndDate();
  }
}
